/**
 * Rebeca chat — high-intent signal detection.
 *
 * Returns a normalized intent label when a candidate message expresses
 * strong intent to convert (apply, connect with a recruiter, share resume,
 * receive a follow-up call, etc.). Used by RebecaMiguelChatbot to
 * proactively surface the lead-capture form mid-conversation instead of
 * waiting for the 2-message timed CTA.
 *
 * The same patterns are mirrored in /app/backend/ai_service.py so the
 * backend can attach `intent` to the chat response payload for analytics.
 */

const PATTERNS = [
  // Apply / start
  /\b(ready|want(s|ed)?|would like)\s+to\s+apply\b/i,
  /\b(let me|I'?ll|i\s+will)\s+apply\b/i,
  /\bapply\s+(now|today|here)\b/i,
  /\bhow\s+(do|can|should)\s+i\s+(apply|sign\s*up|register)\b/i,
  /\bsign\s+me\s+up\b/i,
  /\b(let'?s|let\s+us)\s+(get\s+started|do\s+(this|it)|begin)\b/i,
  /\bi\s+want\s+to\s+(start|begin|move\s+forward)\b/i,
  /\bi'?m\s+(in|interested)\b/i,
  /\b(interested|count\s+me\s+in)\b.*\b(role|position|job|opportunity)\b/i,

  // Recruiter contact
  /\b(connect|put)\s+me\s+(with|to)\s+(a\s+)?recruiter\b/i,
  /\b(talk|speak|chat|connect)\s+(to|with)\s+(a\s+|the\s+)?recruiter\b/i,
  /\bhuman\s+recruiter\b/i,
  /\breach\s+out\s+to\s+me\b/i,
  /\b(follow|circle)\s+(up|back)\s+with\s+me\b/i,
  /\b(call|contact|text|email)\s+me\b/i,
  /\bgive\s+me\s+a\s+call\b/i,
  /\bhave\s+(a\s+)?recruiter\s+(call|reach|contact)\b/i,

  // Resume / next step
  /\bsubmit\s+(my\s+)?resume\b/i,
  /\bsend\s+(my\s+|over\s+my\s+)?(resume|cv)\b/i,
  /\bhere'?s\s+my\s+(resume|email|phone|number)\b/i,
  /\bwhat'?s\s+the\s+next\s+step\b/i,

  // Direct intent
  /\bi'?d\s+like\s+to\s+be\s+contacted\b/i,
  /\bplease\s+contact\s+me\b/i,
];

const HIGH_PRIORITY_PATTERNS = [
  /\b(apply|recruiter|resume|contact me|call me|sign me up)\b/i,
];

/**
 * Detects high-intent signals in a candidate message.
 * @param {string} text  Raw user message
 * @returns {{ intent: string|null, score: number }}
 *   intent — "connect_recruiter" when matched, otherwise null
 *   score  — heuristic 0..1
 */
export function detectIntent(text) {
  const msg = (text || "").trim();
  if (msg.length < 4) return { intent: null, score: 0 };

  let hits = 0;
  for (const re of PATTERNS) {
    if (re.test(msg)) {
      hits += 1;
      if (hits >= 1) break;
    }
  }
  if (hits === 0) return { intent: null, score: 0 };

  const highPriority = HIGH_PRIORITY_PATTERNS.some((re) => re.test(msg));
  return {
    intent: "connect_recruiter",
    score: highPriority ? 0.9 : 0.6,
  };
}
